import { useState, useMemo, memo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { lookupApi } from '../../api/lookup.js';
import { prefsApi } from '../../api/prefs.js';
import { Spinner, ErrorBox, Field } from '../../components/ui.js';
import { Cell } from '../../components/Cell.js';
import { stripImgTags, localDate, localDateMonthsAgo } from '../../lib/format.js';
import { ReconcilePanel } from './ReconcilePanel.js';
import type { ReconcileResult, LookupResult } from '../../api/lookup.js';
import type { ReportColumnMeta } from '../../api/reports.js';

/**
 * 코드 붙여넣기 도구. 정의 하나로 그려진다 (/lookup/:id).
 *
 * 원본들(item_soldout_check.php 등)은 폼을 제출하면 페이지가 통째로 바뀌고
 * 결과가 엑셀 다운로드로 떨어졌다. 여기서는 같은 화면에서 결과를 보고,
 * 필요하면 CSV 로 받는다.
 */

/**
 * 조회 기간 시작의 기본값. 대부분은 올해 1/1 이지만, 조회량이 많은 화면은
 * 기본을 1달 전으로 좁혀둔다 (종합 품절체크, 입고 리스트).
 */
function defaultFromDate(lookupId: string): string {
  if (lookupId === 'soldout-check-all' || lookupId === 'inout-history') {
    return localDateMonthsAgo(1);
  }
  return `${new Date().getFullYear()}-01-01`;
}

/** 화면 설정을 저장하는 키. 다른 조회 도구도 lookup:<id> 로 자기 것을 갖는다. */
function screenIdOf(lookupId: string): string {
  return `lookup:${lookupId}`;
}

interface ColumnPref {
  /** 고정(pinned) 아닌 컬럼 중 보이게 켜 둔 것들의 key. */
  visibleColumns: string[];
}

export function LookupPage() {
  const { lookupId = '' } = useParams();
  const [keys, setKeys] = useState('');
  const [withoutOptions, setWithoutOptions] = useState(false);
  // toISOString() 은 늘 UTC 라, 한국에서 자정~오전 9시에는 어제가 나온다 -
  // 기간별판매량을 보는 화면에서 오늘 매출이 기본값에서 빠져버린다.
  const today = localDate();
  const [from, setFrom] = useState(() => defaultFromDate(lookupId));
  const [to, setTo] = useState(today);
  const [filter, setFilter] = useState('');
  const [useAltKey, setUseAltKey] = useState(false);

  const meta = useQuery({
    queryKey: ['lookup', lookupId, 'meta'],
    queryFn: () => lookupApi.meta(lookupId),
  });

  // 표에서 켜고 끈 컬럼 - DB 에 저장해서 다른 PC 에서 열어도 같은 구성이 보이게
  // 한다(brower localStorage 대신). 고정(pinned) 컬럼은 끌 수 없어 이 목록에
  // 안 들어간다.
  const columnPref = useQuery({
    queryKey: ['prefs', screenIdOf(lookupId)],
    queryFn: () => prefsApi.get<ColumnPref>(screenIdOf(lookupId)),
    enabled: Boolean(lookupId),
  });
  const saveColumnPref = useMutation({
    mutationFn: (visibleColumns: string[]) =>
      prefsApi.save(screenIdOf(lookupId), { visibleColumns }),
  });
  // 사용자가 이번 화면에서 직접 고른 값이 있으면 그걸 최종으로 친다 - 저장
  // 요청이 오가는 동안 다시 서버 값으로 되돌아가 깜빡이지 않게.
  const [manualVisible, setManualVisible] = useState<string[] | null>(null);

  const isReconcile = Boolean(meta.data?.reconcile);

  // 대조 모드와 조회 모드가 다른 모양을 돌려준다. 화면에서 갈라 쓴다.
  const run = useMutation<LookupResult | ReconcileResult>({
    mutationFn: () =>
      isReconcile
        ? lookupApi.reconcile(lookupId, keys, filter)
        : lookupApi.run(lookupId, keys, { withoutOptions, from, to, useAltKey }),
  });

  // CSV 는 서버가 같은 조건으로 다시 조회해 만든다 (아래 downloadCsv 참고).
  const exportCsv = useMutation({
    mutationFn: () => lookupApi.exportCsv(lookupId, keys, { withoutOptions, from, to, useAltKey }),
    onSuccess: (blob) => triggerDownload(blob, `${meta.data?.title ?? lookupId}.csv`),
  });

  // 사진 포함 엑셀. meta.excelExport 인 도구에서만 단추가 보인다. 상품마다
  // CDN 사진을 실제로 내려받아 박으므로 CSV 보다 오래 걸린다.
  const exportExcel = useMutation({
    mutationFn: () => lookupApi.exportExcel(lookupId, keys, { withoutOptions, from, to, useAltKey }),
    onSuccess: (blob) => triggerDownload(blob, `${meta.data?.title ?? lookupId}.xlsx`),
  });

  const codeCount = keys.split(/[\r\n,\t ]+/).filter((s) => s.trim() !== '').length;

  // 고정 컬럼은 항상 보인다 - 켜고 끄는 목록에는 안 넣는다.
  //
  // useMemo 로 참조를 고정한다 - 안 그러면 textarea 에 한 글자 칠 때마다(이
  // 컴포넌트가 다시 렌더될 때마다) 새 Set 이 만들어져, 결과 표(PlainResultPanel,
  // memo 로 감쌌다)가 매번 다시 그려진다. 아래 useState/useQuery 는 조건부
  // return(meta.isPending 등) 보다 앞에 있어야 한다 (Hooks 규칙).
  const optionalColumns = useMemo(
    () => (meta.data?.columns ?? []).filter((c) => !c.hidden && !c.pinned),
    [meta.data],
  );
  const optionalKeys = useMemo(() => optionalColumns.map((c) => c.key), [optionalColumns]);
  const savedVisible = useMemo(
    () => columnPref.data?.pref?.visibleColumns?.filter((k) => optionalKeys.includes(k)),
    [columnPref.data, optionalKeys],
  );
  const visibleOptional = useMemo(
    () => new Set(manualVisible ?? savedVisible ?? optionalKeys),
    [manualVisible, savedVisible, optionalKeys],
  );

  function toggleColumn(key: string) {
    const base = manualVisible ?? savedVisible ?? optionalKeys;
    const next = base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    setManualVisible(next);
    saveColumnPref.mutate(next);
  }

  if (meta.isPending) return <Spinner />;
  if (meta.isError) return <ErrorBox error={meta.error} onRetry={() => void meta.refetch()} />;

  const result = run.data;
  // 조회 모드일 때만 쓰는 결과. 대조 모드는 ReconcilePanel 이 따로 그린다.
  const plain = isReconcile ? undefined : (result as LookupResult | undefined);

  // 컬럼이 많은 표(종합 품절체크는 25개)는 1600px 통 안에서 글자가 다 뭉개진다.
  // 그런 화면만 창 전체 폭을 쓰게 한다 (원본도 표를 창 폭에 꽉 채웠다).
  const wide = meta.data.columns.filter((c) => !c.hidden).length > 12;

  return (
    <section className={wide ? 'page--full' : undefined}>
      <header className="page-head">
        <div>
          <h1 className="page-title">{meta.data.title}</h1>
          {meta.data.description && <p className="page-desc">{meta.data.description}</p>}
        </div>
        <div className="page-head__actions">
          {/* 결과가 있어야 뜬다 - 보이지도 않는 표의 컬럼을 미리 고르게 할 이유가 없고,
              altKey 라디오처럼 같은 라벨을 쓰는 다른 조작과 뒤섞이지도 않는다. */}
          {plain && plain.rows.length > 0 && optionalColumns.length > 0 && (
            <ColumnPicker columns={optionalColumns} visible={visibleOptional} onToggle={toggleColumn} />
          )}
          {plain && plain.rows.length > 0 && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={exportCsv.isPending}
              onClick={() => exportCsv.mutate()}
            >
              {exportCsv.isPending ? '받는 중…' : 'CSV 내려받기'}
            </button>
          )}
          {meta.data.excelExport && plain && plain.rows.length > 0 && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={exportExcel.isPending}
              onClick={() => exportExcel.mutate()}
              title="원본과 같이 사진을 실제로 박은 .xlsx 를 받습니다. 상품 수만큼 시간이 걸립니다."
            >
              {exportExcel.isPending ? '사진 받는 중…' : '엑셀 다운로드 (사진 포함)'}
            </button>
          )}
        </div>
      </header>

      <form
        className="lookup-form"
        onSubmit={(e) => {
          e.preventDefault();
          run.mutate();
        }}
      >
        {meta.data.altKey && (
          <div className="lookup-kinds">
            <label className="checkbox">
              <input
                type="radio"
                name={`altKey-${lookupId}`}
                checked={!useAltKey}
                onChange={() => setUseAltKey(false)}
              />
              {meta.data.inputLabel}
            </label>
            <label className="checkbox">
              <input
                type="radio"
                name={`altKey-${lookupId}`}
                checked={useAltKey}
                onChange={() => setUseAltKey(true)}
              />
              {meta.data.altKey.label}
            </label>
          </div>
        )}

        <Field
          label={`${
            useAltKey && meta.data.altKey ? meta.data.altKey.label : meta.data.inputLabel
          } (한 줄에 하나, 최대 ${meta.data.maxKeys}개)`}
        >
          <textarea
            className="input lookup-input"
            value={keys}
            // 엑셀 텍스트 셀을 복사하면 값이 큰따옴표로 감싸져 들어온다.
            // 붙여넣는 즉시 지워서, 서버가 숫자 아닌 줄로 보고 조용히 버리기 전에 없앤다.
            onChange={(e) => setKeys(e.target.value.replace(/"/g, ''))}
            placeholder={
              useAltKey && meta.data.altKey
                ? meta.data.altKey.placeholder
                : meta.data.inputPlaceholder
            }
            rows={8}
            spellCheck={false}
          />
        </Field>

        {meta.data.reconcile && (
          <div className="lookup-range">
            <Field label={meta.data.reconcile.filterLabel}>
              <input
                className="input"
                value={filter}
                placeholder={meta.data.reconcile.filterPlaceholder}
                onChange={(e) => setFilter(e.target.value)}
              />
            </Field>
          </div>
        )}

        {meta.data.dateRange && (
          <div className="lookup-range">
            <Field label="조회 기간 시작">
              <input className="input" type="date" value={from} max={to}
                onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="조회 기간 끝">
              <input className="input" type="date" value={to} min={from}
                onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
        )}

        <div className="lookup-actions">
          <span className="lookup-count">
            {codeCount > 0
              ? `${codeCount.toLocaleString()}개 입력됨`
              : isReconcile
                // 대조 모드는 원본(item_code_stock_exec.php)도 코드 없이 조건만으로
                // 돌아간다 - 수입처 코드에 걸리는 상품을 통째로 훑어볼 때 쓴다.
                ? '코드 없이 조건만으로도 조회할 수 있습니다'
                : '코드를 넣어주세요'}
          </span>

          {meta.data.hasSimple && (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={withoutOptions}
                onChange={(e) => setWithoutOptions(e.target.checked)}
              />
              옵션 없이 (상품당 한 줄)
            </label>
          )}

          <button
            className="btn btn--primary"
            type="submit"
            disabled={
              run.isPending || (isReconcile ? filter.trim() === '' : codeCount === 0)
            }
          >
            {run.isPending ? '조회 중…' : '조회'}
          </button>
        </div>
      </form>

      {run.isError && <ErrorBox error={run.error} />}

      {result && isReconcile && <ReconcilePanel result={result as ReconcileResult} />}

      {plain && <PlainResultPanel result={plain} visibleOptional={visibleOptional} />}
    </section>
  );
}

/**
 * 서버가 만들어 준 CSV Blob 을 내려받게 띄운다.
 *
 * CSV 를 만드는 일 자체는 여기서 안 한다 - 그건 서버 lib/csv.ts 가 reports 와
 * 함께 쓰는 자리다. 예전엔 이 파일이 직접 CSV 를 만들었는데, 그 사본이
 * valueLabels 변환을 놓쳐 화면엔 '진열' 이 보이는데 받은 파일엔 코드값 'Y' 가
 * 찍혔었다.
 */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 조회 결과 표. 최대 2,000행까지 페이지 없이 그린다.
 *
 * LookupPage 는 이 표와 붙여넣기 입력칸(keys)을 같은 컴포넌트에 두고 있다.
 * memo 로 감싸지 않으면 textarea 에 한 글자 칠 때마다(디바운스는 실제 조회
 * 요청만 늦출 뿐, 렌더는 안 늦춘다) result 가 안 바뀌었는데도 이 표 전체가
 * 다시 그려진다 - 2,000행 x 컬럼마다 매 키 입력에 리렌더가 걸린다.
 */
/**
 * 왼쪽에 고정(pinned)할 컬럼들의 sticky left 오프셋(px). pinned 컬럼은 항상
 * cols 의 맨 앞부터 붙어 있어야 한다(ReportColumn.pinned 주석 참고) - 그래야
 * "이전 pinned 컬럼들의 폭을 더한 값"이 실제 화면 위치와 맞는다.
 */
function pinnedOffsets(cols: ReportColumnMeta[]): Map<string, number> {
  const offsets = new Map<string, number>();
  let left = 0;
  for (const c of cols) {
    if (!c.pinned) break;
    offsets.set(c.key, left);
    left += c.width ? parseInt(c.width, 10) || 0 : 0;
  }
  return offsets;
}

const PlainResultPanel = memo(function PlainResultPanel({
  result,
  visibleOptional,
}: {
  result: LookupResult;
  /** 고정 아닌 컬럼 중 보이게 켜 둔 것들의 key. undefined 면 전부 보인다. */
  visibleOptional?: Set<string>;
}) {
  const cols = result.columns.filter(
    (c) => !c.hidden && (c.pinned || !visibleOptional || visibleOptional.has(c.key)),
  );
  const offsets = pinnedOffsets(cols);
  const lastPinnedKey = [...offsets.keys()].at(-1);
  return (
    <>
      <div className="summary">
        <div className="summary__item">
          <span className="summary__label">넣은 코드</span>
          <strong className="summary__value">{result.requested.toLocaleString()}</strong>
        </div>
        <div className="summary__item">
          <span className="summary__label">찾은 행</span>
          <strong className="summary__value">{result.rows.length.toLocaleString()}</strong>
        </div>
        <div className="summary__item">
          <span className="summary__label">못 찾은 코드</span>
          <strong className="summary__value">{result.missing.length.toLocaleString()}</strong>
        </div>
      </div>

      {result.missing.length > 0 && (
        <div className="lookup-missing">
          <strong>못 찾은 코드 {result.missing.length}개</strong>
          {/* 그대로 복사해서 다시 쓸 수 있게 한 줄에 하나씩 둔다. */}
          <textarea className="input" readOnly rows={3} value={result.missing.join('\n')} />
        </div>
      )}

      {result.rows.length === 0 ? (
        <p className="page-desc">넣은 코드로 찾은 것이 없습니다.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    className={[
                      c.pinned ? (c.key === lastPinnedKey ? 'th-pinned th-pinned--last' : 'th-pinned') : '',
                      // 원본 종합 품절체크의 재고량 - 초록 머리칸 (2026-10-19 요청).
                      c.highlight === 'stock' ? 'th-stock' : '',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined}
                    style={{
                      textAlign: c.align ?? 'left',
                      width: c.width,
                      left: c.pinned ? offsets.get(c.key) : undefined,
                    }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, i) => (
                <tr key={`${String(row[Object.keys(row)[0] ?? ''] ?? '')}-${i}`}>
                  {cols.map((c) => (
                    <td
                      key={c.key}
                      className={[
                        c.clip ? 'td-clip' : '',
                        c.highlight === 'stock' ? 'td-stock' : '',
                        c.pinned ? (c.key === lastPinnedKey ? 'td-pinned td-pinned--last' : 'td-pinned') : '',
                      ]
                        .filter(Boolean)
                        .join(' ') || undefined}
                      // 잘린 칸은 마우스를 올리면 전체가 보여야 한다. 상품명에
                      // 섞인 <img> 아이콘 태그는 툴팁에선 군더더기라 뺀다.
                      title={c.clip ? stripImgTags(row[c.key]) || undefined : undefined}
                      style={{
                        textAlign: c.align ?? 'left',
                        width: c.width,
                        // .td-clip 의 기본 상한(260px)보다 정의에 적은 폭이 이긴다.
                        maxWidth: c.clip ? c.width : undefined,
                        left: c.pinned ? offsets.get(c.key) : undefined,
                      }}
                    >
                      <Cell column={c} row={row} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
});

/**
 * 표에서 보여줄 컬럼을 고른다. 컬럼이 많은 표(종합 품절체크 25개)에서 지금
 * 필요 없는 걸 끄면 가로 스크롤이 줄거나 아예 사라진다.
 *
 * <details>/<summary> 를 그대로 쓴다 - 열림 상태·바깥 클릭 닫기·키보드 포커스를
 * 브라우저가 공짜로 해준다. 고른 값은 LookupPage 가 DB(prefsApi)에 저장한다.
 */
function ColumnPicker({
  columns,
  visible,
  onToggle,
}: {
  columns: ReportColumnMeta[];
  visible: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <details className="col-picker">
      <summary className="btn btn--ghost btn--sm">표시컬럼 설정 ({visible.size}/{columns.length})</summary>
      <div className="col-picker__panel">
        {columns.map((c) => (
          <label key={c.key} className="checkbox col-picker__item">
            <input
              type="checkbox"
              checked={visible.has(c.key)}
              onChange={() => onToggle(c.key)}
            />
            {c.label}
          </label>
        ))}
      </div>
    </details>
  );
}
